import { CommonModule, NgClass, NgIf } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AppState } from '@app/types/app.types';
import { IonIcon, IonButton, IonAlert } from '@ionic/angular/standalone';
import { select, Store } from '@ngrx/store';
import { addIcons } from 'ionicons';
import { chevronForwardSharp } from 'ionicons/icons';
import { filter, Observable, Subject, takeUntil } from 'rxjs';
import { CustomConaTooltipComponent } from '../custom-cona-tooltip/custom-cona-tooltip.component';
import { ApiService } from '@app/services/api.service';
import { ToasterService } from '@app/services/toaster.service';
import { ToastMessages } from '@app/constants/toast-messages.constants';
import type { OverlayEventDetail } from '@ionic/core';

// --- These are the constants for our manual calculation ---
const MENU_ITEM_HEIGHT = 80; // 12(pad) + 24(icon) + 8(gap) + 10(title) + 12(pad)
const INITIAL_PADDING = 8; // padding-top of .menu-list

@Component({
  selector: 'app-left-side-bar',
  templateUrl: './left-side-bar.component.html',
  styleUrls: ['./left-side-bar.component.css'],
  standalone: true,
  imports: [CommonModule, NgClass, NgIf, IonIcon, IonButton, IonAlert, CustomConaTooltipComponent],
})
export class LeftSideBarComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChildren('listItem') listItems!: QueryList<ElementRef>;

  isExpanded: boolean;
  highlighterPos: number;
  selectedPageHref: string;
  screenAccess$!: Observable<string[]>;
  private destroy$ = new Subject<void>();
  private currentScreenAccess: string[] = []; // Store the current access rights

  constructor(
    private apiService: ApiService,
    private toastService: ToasterService,
    private router: Router,
    private store: Store<AppState>,
  ) {
    addIcons({ chevronForwardSharp });
    this.isExpanded = false;
    this.highlighterPos = INITIAL_PADDING;
    this.selectedPageHref = this.router.url;
  }

  ngOnInit(): void {
    this.screenAccess$ = this.store.pipe(select((state: any) => state.app.screenAccessState));

    // Subscribe to screenAccess to know which items are visible
    this.screenAccess$.pipe(takeUntil(this.destroy$)).subscribe((access) => {
      this.currentScreenAccess = access;
      // When access rights change, the menu re-renders, so we must update the position.
      this.updateHighlighterPosition();
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        this.selectedPageHref = event.urlAfterRedirects;
        this.updateHighlighterPosition();
      });
  }

  ngAfterViewInit(): void {
    // When the menu items first appear, this will fire.
    this.listItems.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateHighlighterPosition();
    });

    // A single initial call.
    this.updateHighlighterPosition();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateHighlighterPosition(): void {
    // No timeout needed. This logic is instant.
    if (
      !this.listItems ||
      this.listItems.length === 0 ||
      !this.currentScreenAccess ||
      this.currentScreenAccess.length === 0
    ) {
      return;
    }

    // Define the full, ordered list of all possible menu items.
    const allMenuItems = [
      { href: '/', requiredAccess: ['RECS', 'ALL'] },
      { href: '/void', requiredAccess: ['VOIDS', 'ALL'] },
      { href: '/history', requiredAccess: ['RECS', 'ALL'] },
      // Add other menu items here in the same order as the HTML
    ];

    // Filter this list to get only the ones that are currently VISIBLE.
    const visibleMenuItems = allMenuItems.filter((item) =>
      item.requiredAccess.some((access) => this.currentScreenAccess.includes(access)),
    );

    // Find the index of the active route within the VISIBLE items.
    const activeIndexInVisible = visibleMenuItems.findIndex((item) => {
      if (item.href === '/') {
        return this.getIsHomePage();
      }
      return this.selectedPageHref.startsWith(item.href);
    });

    if (activeIndexInVisible > -1) {
      // CALCULATE the position based on the index in the VISIBLE list.
      const newPos = activeIndexInVisible * MENU_ITEM_HEIGHT + INITIAL_PADDING;
      this.highlighterPos = newPos;
      console.log(
        `✅ SUCCESS [Calculated]: Highlighter position set to ${this.highlighterPos} for visible index ${activeIndexInVisible} (${this.selectedPageHref})`,
      );
    } else {
      this.highlighterPos = -100;
    }
  }

  // --- Your other component methods ---

  toggleSidebar() {
    this.isExpanded = !this.isExpanded;
  }

  getIsHomePage(): boolean {
    if (this.selectedPageHref === '/') {
      return true;
    }
    const firstSegment = this.selectedPageHref.split('/')[1];
    return ['summary', 'session'].includes(firstSegment);
  }

  onMenuItemClick(event: MouseEvent) {
    event.preventDefault();
    const element = event.currentTarget as HTMLDivElement;
    const dataHref = element.getAttribute('data-href');
    if (dataHref) {
      this.router.navigateByUrl(dataHref);
    }
  }

  public alertButtons = [
    {
      text: 'Cancel',
      role: 'cancel',
      handler: () => {},
    },
    {
      text: 'OK',
      role: 'confirm',
      handler: () => {
        this.apiService.downloadUserGuide().subscribe({
          next: (pdfBlob: Blob) => {
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'User Guide.pdf';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            this.toastService.success({ message: ToastMessages.export.success });
          },
          error: (error: any) => {
            this.toastService.error({ message: ToastMessages.export.error, payload: { message: error.message } });
          },
        });
      },
    },
  ];

  supportMenu() {
  }

  setResult(event: CustomEvent<OverlayEventDetail>) {
  }
}
